import { useEffect, useRef, useState } from "react";

/*
 * The "Sign in with Google" button.
 *
 * INJECTED, NOT INSTALLED, like useGoogleMaps and usePayPal before it. A
 * dependency means editing front/package.json and re-resolving a 2021 tree
 * (CLAUDE.md section 4), and a script in index.html would load Google's
 * identity library on the blog, the area pages and the 404 - none of which
 * sign anybody in.
 *
 * WHAT COMES BACK IS NOT PROOF OF ANYTHING, and the shape of this file says
 * so: the callback hands the page a `credential`, which goes straight to the
 * server. It is an ID token signed by Google, and the only thing that makes
 * it true is the signature check in api/data/googleIdToken.js. Nothing is
 * read out of it here - not the email, not the name - because anything read
 * in the browser is something an attacker can simply write.
 */

const SRC = "https://accounts.google.com/gsi/client";

/* Module-level, so two mounts share one script tag rather than racing. */
let loading = null;

function load() {
    if (loading) return loading;

    loading = new Promise((resolve, reject) => {
        if (window.google && window.google.accounts && window.google.accounts.id) {
            resolve(window.google);
            return;
        }
        const script = document.createElement("script");
        script.src = SRC;
        script.async = true;
        script.defer = true;
        script.onload = () => {
            if (window.google && window.google.accounts && window.google.accounts.id) {
                resolve(window.google);
            } else {
                reject(new Error("Google loaded but exposed nothing"));
            }
        };
        script.onerror = () => {
            /*
             * Forgotten, so a later attempt can retry. A cached rejection
             * would let one blocked request - an ad blocker, a dropped
             * connection - disable Google sign-in for the whole session.
             */
            loading = null;
            reject(new Error("Google sign-in could not be loaded"));
        };
        document.body.appendChild(script);
    });

    return loading;
}

/**
 * Renders Google's button into a ref'd element and calls [onCredential] with
 * the ID token.
 *
 * @param clientId public, from REACT_APP_GOOGLE_CLIENT_ID
 * @returns { ref, ready, error } - ref goes on an empty div
 */
export default function useGoogleSignIn(clientId, onCredential) {
    const ref = useRef(null);
    const [ready, setReady] = useState(false);
    const [error, setError] = useState(null);

    /*
     * The handler in a ref, not closed over. Google keeps whatever callback
     * it was initialised with; re-rendering React does not replace it, so a
     * closure would leave the button calling the first render's function -
     * which, on a sign-in page, means posting a token into a dead handler
     * and nothing happening at all.
     */
    const handler = useRef(onCredential);
    handler.current = onCredential;

    useEffect(() => {
        let alive = true;
        if (!clientId) return undefined;

        load()
            .then((google) => {
                if (!alive || !ref.current) return;

                google.accounts.id.initialize({
                    client_id: clientId,
                    callback: (response) => {
                        if (response && response.credential) {
                            handler.current(response.credential);
                        }
                    },
                    /*
                     * No One Tap. It appears unbidden over the page, and on a
                     * checkout that is a prompt somebody has to dismiss
                     * before they can pay. The button is asked for; the
                     * overlay is not.
                     */
                    auto_select: false,
                    cancel_on_tap_outside: true,
                });

                google.accounts.id.renderButton(ref.current, {
                    theme: "outline",
                    size: "large",
                    width: 280,
                    text: "continue_with",
                    shape: "rectangular",
                });

                if (alive) setReady(true);
            })
            .catch((e) => {
                if (alive) setError(e.message || "Google sign-in is unavailable");
            });

        return () => {
            alive = false;
        };
    }, [clientId]);

    return { ref, ready, error };
}
